import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { match, P } from 'ts-pattern';
import { HTMLContent } from '@/components/ui/html-content';
import type { CMSFaqItem, CMSNestedComponent } from '../schemas';
import { cn } from '@/lib/utils';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import React from 'react';

interface CMSFaqsProps {
  faqs: CMSFaqItem[];
  hasTabHeading: boolean;
}

const FaqContent = ({ item }: { item: CMSNestedComponent }) => {
  return match(item)
    .with({ type: 'rich_text' }, (contentItem) => (
      <div key={contentItem.id}>
        {contentItem.text_advanced.map((textItem, idx) =>
          match(textItem)
            .with({ type: 'text', text: P.string }, (textNode) => (
              <HTMLContent
                key={idx}
                content={textNode.text}
                className={cn(
                  'md:pr-[58px] lg:pr-[81px]',
                  'text-sm md:text-base lg:text-lg text-black mt-4',

                  // H1 text sizes
                  '[&_h1]:text-xl md:[&_h1]:text-2xl lg:[&_h1]:text-[52px] lg:[&_h1]:leading-[72px]',
                  // H2 text sizes
                  '[&_h2]:text-lg md:[&_h2]:text-2xl lg:[&_h2]:text-3xl',
                  // H3 text sizes
                  `[&_h3]:text-base md:[&_h3]:text-lg lg:[&_h3]:text-xl`,

                  //Add margin top to h1 tag itself
                  '[&_h1:not(:first-child)]:mt-8 md:[&_h1:not(:first-child)]:mt-12 lg:[&_h1:not(:first-child)]:mt-20',

                  //Add margin top to h2 tag itself
                  '[&_h2:not(:first-child)]:mt-8 md:[&_h2:not(:first-child)]:mt-8 lg:[&_h2:not(:first-child)]:mt-8',

                  //Add margin top to h3 tag itself
                  '[&_h3:not(:first-child)]:mt-6 md:[&_h3:not(:first-child)]:mt-8',

                  // Add spacing between p tags
                  '[&_p]:mt-2 md:[&_p]:mt-4',

                  // Add spacing for image tags
                  '[&_img]:my-2 md:[&_img]:my-4',

                  // style for links (a tag)
                  '[&_p_a]:underline [&_p_a]:font-nunito500',

                  // Add margin inline auto for image inside h1,h2,h3 and p tag if parent tag has text align center
                  '[&_h1[style*="text-align:_center"]_img]:mx-auto',
                  '[&_h2[style*="text-align:_center"]_img]:mx-auto',
                  '[&_h3[style*="text-align:_center"]_img]:mx-auto',
                  '[&_p[style*="text-align:_center"]_img]:mx-auto',

                  // Add default style for table
                  '[&_table]:border [&_table]:border-deep-teal-50 [&_td]:border [&_td]:border-deep-teal-50 [&_td]:px-4',

                  //Add style for <hr/>
                  '[&_hr]:my-4 lg:[&_hr]:my-6',

                  //Add style for ordered list
                  '[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-3 lg:[&_ol]:pl-7',

                  // Add bold for strong tag
                  '[&_strong]:font-nunito700',

                  //Add style for p tag tag inside p tag
                  '[&_p]:font-nunito300'
                )}
              />
            ))
            .otherwise(() => null)
        )}
      </div>
    ))
    .otherwise(() => null);
};

const CMSFaqs = ({ faqs, hasTabHeading }: CMSFaqsProps) => {
  const [hiddenContentIds, setHiddenContentIds] = React.useState<Set<string>>(
    new Set(faqs.map((faq) => faq.id))
  );
  return (
    <Accordion type="single" collapsible data-component="CMSFaqs">
      {faqs.map((faq) => (
        <AccordionItem
          className={cn({
            'first-of-type:pt-0 md:first-of-type:pt-0': !hasTabHeading,
          })}
          key={faq.id}
          value={faq.id}
        >
          <AccordionTrigger
            onClick={() => {
              setHiddenContentIds((prev) => {
                const next = new Set(prev);
                next.delete(faq.id);
                return next;
              });
            }}
          >
            {faq.title}
          </AccordionTrigger>
          {hiddenContentIds.has(faq.id) ? (
            <VisuallyHidden.Root>
              {faq.content.map((contentItem, idx) => (
                <FaqContent
                  key={`${faq.id}-hidden-${idx}`}
                  item={contentItem}
                />
              ))}
            </VisuallyHidden.Root>
          ) : null}
          <AccordionContent>
            {faq.content.map((contentItem, idx) => (
              <FaqContent key={`${faq.id}-visible-${idx}`} item={contentItem} />
            ))}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
};
export { CMSFaqs };
